<template>
  <div class="calendarlist w-dropdown-list">
    <div class="month month-left">
      <div class="calendarhead">
        <div class="chevron" @click="goPrevMonth">
          <ChevronLeftIcon />
        </div>
        <div class="text-calendarhead">
          {{ monthsLocale[activeMonthStart] + ' ' + activeYearStart }}
        </div>
        <div class="chevron" @click="goNextMonth">
          <ChevronRightIcon />
        </div>
      </div>

      <div class="calendarrow w-clearfix">
        <div v-for="item in shortDaysLocale" :key="item" class="calendardayname">
          <div class="text-calendardayname">{{ item }}</div>
        </div>
      </div>

      <div v-for="week in maxWeeks" :key="week" class="calendarrow w-clearfix">
        <!-- Next <div> element may have one of the classes:
        day-in-range, day-selected and day-disabled (if not in this month) -->
        <div
          v-for="day in numOfDays"
          :key="day"
          :class="[...getCellClass(week, day, startMonthDay, endMonthDate), 'day']"
          class="calendarday"
          @click="selectFirstItem(week, day)"
        >
          <div v-html="getDayCell(week, day, startMonthDay, endMonthDate)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ChevronLeftIcon from '@/icons/chevron-left.svg';
  import ChevronRightIcon from '@/icons/chevron-right.svg';
  import fecha from 'fecha';

  const dayClasses = {
    disabled: 'calendarday-disabled',
    selected: 'calendarday-selected',
    inRange: 'calendarday-middle',
    today: 'calendarday-today',
    start: null,
    end: null,
  };

  const monthLocale = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const daysLocale = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  export default {
    name: 'DateRange',
    components: {
      ChevronLeftIcon,
      ChevronRightIcon,
    },
    props: {
      dateOfStart: Date,
      dateOfEnd: Date,
      dateFormat: String,
      isRange: {
        type: Boolean,
        default: true,
      },
      isActionsNeeded: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        dateRange: {
          start: this.dateOfStart || null,
          end: this.dateOfEnd || null,
        },
        numOfDays: 7,
        format: this.dateFormat || 'YYYY-MM-DD',
        isFirstChoice: true,
        activeMonthStart: new Date().getMonth(),
        activeYearStart: new Date().getFullYear(),
        activeYearEnd: new Date().getFullYear(),
        monthsLocale: monthLocale,
        shortDaysLocale: daysLocale,
        isApplyDisabled: true,
      };
    },
    computed: {
      today() {
        return fecha.format(new Date(), 'YYYY-MM-DD');
      },
      startMonthDay() {
        return new Date(Date.UTC(this.activeYearStart, this.activeMonthStart, 0)).getDay();
      },
      endMonthDate() {
        return new Date(Date.UTC(this.activeYearEnd, this.startNextActiveMonth, 0)).getDate();
      },
      maxWeeks() {
        return Math.ceil((this.startMonthDay + this.endMonthDate) / 7);
      },
      startNextActiveMonth: function () {
        return this.activeMonthStart >= 11 ? 0 : this.activeMonthStart + 1;
      },
    },
    watch: {
      startNextActiveMonth: function (value) {
        if (value === 0) this.activeYearEnd = this.activeYearStart + 1;
      },
      dateOfStart: function (val) {
        this.dateRange.start = val || null;
      },
      dateOfEnd: function (val) {
        this.dateRange.end = val || null;
      },
    },
    created () {
      if (this.activeMonthStart === 11) this.activeYearEnd = this.activeYearStart + 1;
    },
    methods: {
      compareDates(date1, date2) {
        // Simple strings comparison using fecha format
        const d1 = fecha.format(new Date(date1), 'YYYY-MM-DD');
        const d2 = fecha.format(new Date(date2), 'YYYY-MM-DD');

        if (d1 === d2) return 0;
        return d1 > d2 ? 1 : -1;
      },
      getDateString(date, format = this.format) {
        if (!date) return null;

        const dateparse = new Date(Date.parse(date));
        return fecha.format(new Date(Date.UTC(dateparse.getFullYear(), dateparse.getMonth(), dateparse.getDate())), format);
      },
      getDayIndexInMonth(week, day, startMonthDay) {
        const date = (this.numOfDays * (week - 1)) + day;
        return date - startMonthDay;
      },
      getDayCell (week, day, startMonthDay, endMonthDate) {
        const result = this.getDayIndexInMonth(week, day, startMonthDay);

        if (result < 1) {
          const end = new Date(Date.UTC(this.activeYearEnd, this.startNextActiveMonth - 1, 0)).getDate();
          return result + end;
        } else if (result > endMonthDate) {
          return result - endMonthDate;
        } else {
          return result;
        }
      },
      getNewDateRange(result, activeMonth, activeYear) {
        const newDate = {};
        let key = 'start';

        if (!this.isFirstChoice && this.isRange) key = 'end';
        else newDate.end = null;

        const resultDate = new Date(Date.UTC(activeYear, activeMonth, result));
        if (!this.isFirstChoice && resultDate < this.dateRange.start) {
          this.isFirstChoice = false;
          return { start: resultDate };
        }
        this.isFirstChoice = !this.isFirstChoice;
        newDate[key] = resultDate;

        return newDate;
      },
      selectFirstItem(week, day) {
        const result = this.getDayIndexInMonth(week, day, this.startMonthDay);

        this.dateRange = Object.assign({}, this.dateRange, this.getNewDateRange(result, this.activeMonthStart, this.activeYearStart));
        if (this.isRange) {
          if (this.dateRange.start !== null && this.dateRange.end !== null) {
            this.isApplyDisabled = false;
            if (!this.isActionsNeeded)
              this.$emit('changeAll', {start: this.getDateString(this.dateRange.start), end: this.getDateString(this.dateRange.end)});
          } else {
            this.isApplyDisabled = true;
          }
        } else {
          this.$emit('pick', this.getDateString(this.dateRange.start));
        }
      },

      getCellClass(week, day, startMonthDay, endMonthDate) {
        const arr = [];

        // Give proper classes for day cells
        if (this.isDateInRange(week, day, startMonthDay, endMonthDate))
          arr.push(dayClasses.inRange);
        if (this.isDateDisabled(week, day, startMonthDay, endMonthDate))
          arr.push(dayClasses.disabled);
        if (this.isToday(week, day, startMonthDay, endMonthDate))
          arr.push(dayClasses.today);

        arr.push(...this.isDateSelected(week, day, startMonthDay, endMonthDate));
        return arr;
      },
      isDateSelected(week, day, startMonthDay) {
        const result = this.getDayIndexInMonth(week, day, startMonthDay);
        const currDate = new Date(Date.UTC(this.activeYearStart, this.activeMonthStart, result));

        // Apply selected classes for days. If object dayClasses has !!dayClasses.start - then
        // apply different classes for start and end dates, also + inRange class for them
        if (this.dateRange.start && !this.dateRange.end && this.compareDates(this.dateRange.start, currDate) === 0) return [dayClasses.selected];
        else if (this.dateRange.start && this.dateRange.end &&
          this.compareDates(this.dateRange.start, this.dateRange.end) === 0 &&
          this.compareDates(this.dateRange.start, currDate) === 0)
          return dayClasses.start ? [dayClasses.inRange, dayClasses.start, dayClasses.end] : [dayClasses.selected];
        else if (this.dateRange.start && this.compareDates(this.dateRange.start, currDate) === 0)
          return dayClasses.start ? [dayClasses.inRange, dayClasses.start] : [dayClasses.selected];
        else if (this.dateRange.end && this.compareDates(this.dateRange.end, currDate) === 0)
          return dayClasses.start ? [dayClasses.inRange, dayClasses.end] : [dayClasses.selected];
        else return [''];
      },
      isDateInRange(week, day, startMonthDay) {
        const result = this.getDayIndexInMonth(week, day, startMonthDay);
        const currDate = new Date(Date.UTC(this.activeYearStart, this.activeMonthStart, result));

        return (this.dateRange.start && this.dateRange.start.getTime() < currDate.getTime()) &&
          (this.dateRange.end && this.dateRange.end.getTime() > currDate.getTime());
      },
      isDateDisabled(week, day, startMonthDay, endMonthDate) {
        const result = this.getDayIndexInMonth(week, day, startMonthDay);
        return !(result > 0 && result <= endMonthDate);
      },
      isToday(week, day, startMonthDay) {
        const result = this.getDayIndexInMonth(week, day, startMonthDay);
        const currDate = fecha.format(new Date(Date.UTC(this.activeYearStart, this.activeMonthStart, result)), 'YYYY-MM-DD');

        return this.today === currDate;
      },

      // Change months by clicking on the arrows
      goPrevMonth() {
        const prevMonth = new Date(Date.UTC(this.activeYearStart, this.activeMonthStart, 0));
        this.activeMonthStart = prevMonth.getMonth();
        this.activeYearStart = prevMonth.getFullYear();
        this.activeYearEnd = prevMonth.getFullYear();
      },
      goNextMonth() {
        const nextMonth = new Date(Date.UTC(this.activeYearEnd, this.startNextActiveMonth, 1));
        this.activeMonthStart = nextMonth.getMonth();
        this.activeYearStart = nextMonth.getFullYear();
        this.activeYearEnd = nextMonth.getFullYear();
      },

      apply() {
        this.$emit('changeAll', {start: this.getDateString(this.dateRange.start), end: this.getDateString(this.dateRange.end)});
      },
      clear() {
        this.isFirstChoice = true;
        this.dateRange = [null, null];
        this.isApplyDisabled = true;
        this.$emit('changeAll', {start: null, end: null}, false);
      },
    },
  };
</script>
